"use client";

import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "next/navigation";
import Decimal from "decimal.js";

import {
  FieldData,
  SelectOptionComponent,
  UpdateFreshField,
} from "@forms/schema";
import { useAPI } from "apps/website/hooks/useAPI";
import {
  ICatExtraDetails,
  ICustomerCatsResponse,
} from "libs/api/customer/src/lib/types/cats";
import {
  PouchSize,
} from "libs/cat-calculations/src/lib/cat-calculations.types";
import { DisplayState } from "@/constants/state";
import { IFreshPricingPlan, ITraySize } from "@api-clients/subscriptions";
import {
  MEAL_PLAN_CALORIES,
} from "libs/cat-calculations/src/lib/cat-calculations.constants";
import { renderStoryBlokRichText } from "apps/website/utils/storyblok/text";
import { getFlowFieldValuesForInterpolation } from "@./state";
import { PricingPlan } from "@/primary-models";
import {
  pricingPlanToRechargeSubscriptionProperty,
  rechargeSubscriptionPropertyToPricingPlan,
} from "@/constants/pricing";
import { serialize, SerialType } from "@utils/types";
import { toCurrencyDecimals } from "@/decimal-util";
import { capitaliseFirst } from "@./string-utils";

import Container from "../../layout/Container/Container";
import Button from "../../base/Button/Button";
import Modal from "../../feature/Modal/Modal";
import Text from "../../base/Text/Text";
import { legacySizeCollectionMap } from "../../base/Text/Text.map";
import Grid from "../../layout/Grid/Grid";
import Column from "../../layout/Column/Column";
import Spacer from "../../layout/Spacer/Spacer";
import Section from "../../layout/Section/Section";
import { AlertPrompt } from "../../feature/AlertPrompt/AlertPrompt";
import FreeFlowRichText from "../../feature/Freeflow/FreeFlowRichText/FreeFlowRichText";

import { FormServiceRadioSelect } from "./RadioSelect";
import { getMealPlanSuggestions } from "./helpers/getMealPlanSuggestions";
import { CancellationReasonForMealPlan } from "./helpers/cancellation-reason";

export interface IFormServiceFreshSizeCadenceSelect {
  field: UpdateFreshField;
  flowId: string;
  optional?: boolean;
  linkingId: string;
  selected?: FieldData;
  helpText: string;
  optionSize: string;
}
export type IFormServiceFreshSizeCadenceSelectOptions = {
  traySizes: SelectOptionComponent<string>[];
  mealsPlans: SelectOptionComponent<string>[];
};

export const FormServiceUpdateFresh: FC<IFormServiceFreshSizeCadenceSelect> = ({
  flowId,
  field,
  optional,
  linkingId,
  selected,
}) => {
  const api = useAPI();

  const searchParams = useSearchParams();

  const [ cats, setCats ] = useState<ICustomerCatsResponse>();
  const [ catId, setCatId ] = useState<string>();
  const [ cat, setCat ] = useState<ICatExtraDetails>();
  const [ options, setOptions ] =
    useState<IFormServiceFreshSizeCadenceSelectOptions>();
  const [ availableTraySizes, setAvailableTraySizes ] =
    useState<SerialType<ITraySize>[]>();
  const [ availableMealPlanTypes, setAvailableMealPlanTypes ] =
    useState<SerialType<IFreshPricingPlan>[]>();
  const [ currentTraySize, setCurrentTraySize ] = useState<PouchSize>();
  const [ currentMealPlan, setCurrentMealPlan ] = useState<PricingPlan>();
  const [ selectedTraySize, setSelectedTraySize ] = useState<PouchSize>();
  const [ selectedMealPlan, setSelectedMealPlan ] =
    useState<PricingPlan>();
  const [ formState, setFormState ] = useState<DisplayState>(DisplayState.READY);
  const [ showSuccessfullyUpdatedModal, setShowSuccessfullyUpdatedModal ] =
    useState<boolean>(false);
  const [ error, setError ] = useState<string>();

  const [ mealPlanCadence, setMealPlanCadence ] = useState<number>();
  const [ mealPlanQuantity, setMealPlanQuantity ] = useState<number>();
  const [ mealPlanTitle, setMealPlanTitle ] = useState<string>();
  const [ mealPlanCalories, setMealPlanCalories ] = useState<number>();

  const getMealPlanSuggestionsCallback = useCallback(() => getMealPlanSuggestions({
    cancellationReason: searchParams.get("cancellationReason") as CancellationReasonForMealPlan | undefined,
    currentMealPlan,
    selectedMealPlan,
  }), [ currentMealPlan, searchParams, selectedMealPlan ]);

  const getCats = useCallback(async () => api.Customer.getFreshCats(), []);
  const getAvailableTraySizes = useCallback(
    async (planType: PricingPlan) => api.Subscriptions.getAvailableTraySizes(planType),
    [ api.Subscriptions ],
  );

  const getAvailableMealPlanTypes = useCallback(
    async () => api.Subscriptions.getAvailableMealPlans(),
    [ api.Subscriptions ],
  );

  const getOptions = useCallback(
    (): IFormServiceFreshSizeCadenceSelectOptions => ({
      traySizes:
        availableTraySizes?.map((tray) => ({
          _uid: tray.traySize as PouchSize,
          title: `${tray.calories} kcal/day`,
          value: tray.traySize as PouchSize,
          component: "select_option",
          selected: currentTraySize === tray.traySize,
          advanced_options: {
            secondaryText: "",
            tag:
              cat?.suggestedTraySize === tray.traySize
                ? "Recommended"
                : undefined,
            tertiaryText: `£${toCurrencyDecimals(
              new Decimal(tray.price).dividedBy((
                availableMealPlanTypes?.find(
                  (type) => type.pricingPlan === currentMealPlan,
                )?.cadence || 1)),
            )}/day`,
            current: currentTraySize === tray.traySize,
          },
        })) || [],
      mealsPlans: getMealPlanSuggestionsCallback(),
    }),
    [
      currentTraySize,
      currentMealPlan,
      availableMealPlanTypes,
      availableTraySizes,
      getMealPlanSuggestionsCallback,
      cat?.suggestedTraySize,
    ],
  );

  const updateSubscription = () => {
    if (
      (selectedMealPlan || currentMealPlan) &&
      (selectedTraySize || currentTraySize) &&
      catId
    ) {
      setFormState(DisplayState.PROCESSING);
      api.Customer.updateFreshSizeAndCadence({
        newSize: selectedTraySize || (currentTraySize as PouchSize),
        newMealPlan:
          pricingPlanToRechargeSubscriptionProperty[selectedMealPlan || (currentMealPlan as PricingPlan)],
        catId,
        source: "Customer",
        cxUser: undefined,
      })
        .then(() => {
          setShowSuccessfullyUpdatedModal(true);
        })
        .catch(() => {
          setError(
            `Could not update ${cat?.cat.name}'s ${field.type === "mealPlan" ? "meal plan" : "tray size"
            }, please try again.`,
          );
        })
        .finally(() => {
          setFormState(DisplayState.READY);
        });
    }
  };

  const getSelectedMealPlan = useMemo(() => {
    if (cat) {
      const catDefaultPlan = availableMealPlanTypes?.find(
        (plan) => plan.pricingPlan === (rechargeSubscriptionPropertyToPricingPlan[cat.mealPlan]
          || PricingPlan.STANDARD),
      );
      if (!selected || field.type === "traySize") {
        return catDefaultPlan;
      }

      return availableMealPlanTypes?.find(
        (plan) => plan.pricingPlan === (rechargeSubscriptionPropertyToPricingPlan[
          selected.submitValue as keyof typeof rechargeSubscriptionPropertyToPricingPlan
        ] || PricingPlan.STANDARD),
      );
    }
  }, [ cat, selected, field.type, availableMealPlanTypes ]);

  const getSelectedPouch = useMemo(() => {
    if (cat) {
      const catDefaultTraySize =
        getSelectedMealPlan?.traySize[cat.mealPlanSize];
      if (!selected || field.type === "mealPlan") {
        return catDefaultTraySize;
      }

      return getSelectedMealPlan?.traySize[selected.submitValue as PouchSize];
    }
  }, [
    cat,
    cats,
    selected,
    field.type,
    getSelectedMealPlan,
    selectedMealPlan,
    selectedTraySize,
  ]);

  const getIsNewPouchSizeSmaller = useMemo(() => {
    if (mealPlanCalories && currentMealPlan) {
      if (mealPlanCalories < MEAL_PLAN_CALORIES[currentTraySize as PouchSize]) {
        return true;
      }
    }

    return false;
  }, [
    cat,
    cats,
    selected,
    field.type,
    getSelectedMealPlan,
    getSelectedPouch,
    currentTraySize,
    selectedMealPlan,
    selectedTraySize,
    mealPlanCalories,
  ]);

  useEffect(() => {
    async function setData() {
      setCats(await getCats());
    }
    void setData();
  }, []);

  useEffect(() => {
    async function setData() {
      const getCatId = searchParams.get("catId");
      if (getCatId) {
        setCatId(decodeURIComponent(decodeURIComponent(getCatId)));
      }
    }
    void setData();
  }, [ cats, searchParams ]);

  useEffect(() => {
    async function setData() {
      if (catId) {
        setCat(cats?.cats[catId]);
      }
      setAvailableMealPlanTypes(serialize(await getAvailableMealPlanTypes()));
    }
    void setData();
  }, [ catId, cats ]);

  useEffect(() => {
    if (cat) {
      setCurrentMealPlan(rechargeSubscriptionPropertyToPricingPlan[
        cat.mealPlan
      ]);
      setCurrentTraySize(cat.mealPlanSize);
    }
  }, [ cat, cats ]);

  useEffect(() => {
    async function setData() {
      if (currentMealPlan) {
        setAvailableTraySizes(serialize(await getAvailableTraySizes(currentMealPlan)));
      }
    }
    void setData();

    if (currentMealPlan && !selectedMealPlan) {
      const mealPlanSuggestions = getMealPlanSuggestionsCallback();
      const [ firstSuggestion ] = mealPlanSuggestions;
      const currentOrFirstSuggestion = mealPlanSuggestions
        .find((suggestion) => suggestion.value === currentMealPlan)
        ?? firstSuggestion;

      setSelectedMealPlan(currentOrFirstSuggestion.value);
    }

    if (!selectedTraySize) {
      setSelectedTraySize(currentTraySize);
    }
  }, [
    currentMealPlan,
    currentTraySize,
    availableMealPlanTypes,
    getMealPlanSuggestionsCallback,
    selectedMealPlan,
  ]);

  useEffect(() => {
    async function setData() {
      setOptions(await getOptions());
    }
    void setData();
  }, [
    currentMealPlan,
    currentTraySize,
    availableTraySizes,
    availableMealPlanTypes,
  ]);

  useEffect(() => {
    const plan = availableMealPlanTypes?.find(
      (mappedPlan) => mappedPlan.pricingPlan === selectedMealPlan,
    );
    if (plan && selectedTraySize) {
      setMealPlanCadence(plan.cadence);
      setMealPlanQuantity(plan.quantity);
      setMealPlanTitle(capitaliseFirst(plan.pricingPlan, true));
      setMealPlanCalories(plan.traySize[selectedTraySize].calories);
    }
  }, [
    selectedMealPlan,
    selectedTraySize,
    availableTraySizes,
    availableMealPlanTypes,
  ]);

  return (
    <>
      { error && (
        <AlertPrompt
          style="error"
          title={ `Error updating ${field.type === "mealPlan" ? "meal plan" : "tray size"
          }` }
        >
          { error }
        </AlertPrompt>
      ) }
      { field.tray_size_introduction && (
        <>
          <FreeFlowRichText
            size={ legacySizeCollectionMap.base }
            withSpacer={ false }
          >
            { renderStoryBlokRichText(field.tray_size_introduction, {
              interpolation: {
                values: getFlowFieldValuesForInterpolation(
                  flowId ?? "UNKNOWN",
                  "0",
                  "GET_RELATED",
                ),
              },
              tags: {
                MARK_CODE: {
                  display: "default",
                },
              },
            }) }
          </FreeFlowRichText>
          <Spacer size="md" />
        </>
      ) }
      { [ "traySize", "both" ].includes(field.type as string) && (
        <FormServiceRadioSelect
          flowId={ flowId }
          field={ {
            ...field,
            component: "radio_select",
            label: "Select tray size",
            hide_label: true,
            name: "traySize",
            options:
              options?.traySizes.map((option) => ({
                ...option,
                advanced_options: {
                  ...option.advanced_options,
                },
              })) || [],
            helpText: "",
            optionSize: "",
          } }
          onChange={ (event) => {
            setSelectedTraySize(event.submitValue as PouchSize);
          } }
          optional={ optional }
          linkingId={ linkingId }
          selected={ {
            submitValue: selectedTraySize,
            displayValue: selectedTraySize,
          } }
          optionStyle="confirmationModal"
        />
      ) }
      { field.meal_plan_introduction && (
        <>
          <Spacer size="xl" />
          <FreeFlowRichText
            size={ legacySizeCollectionMap.base }
            withSpacer={ false }
          >
            { renderStoryBlokRichText(field.meal_plan_introduction, {
              interpolation: {
                values: getFlowFieldValuesForInterpolation(
                  flowId ?? "UNKNOWN",
                  "0",
                  "GET_RELATED",
                ),
              },
            }) }
          </FreeFlowRichText>
          <Spacer size="md" />
        </>
      ) }
      { [ "mealPlan", "both" ].includes(field.type as string) && (
        <FormServiceRadioSelect
          flowId={ flowId }
          field={ {
            ...field,
            component: "radio_select",
            name: "mealPlan",
            label: "Select meal plan",
            hide_label: true,
            options:
              options?.mealsPlans.map((option) => ({
                ...option,
                advanced_options: {
                  ...option.advanced_options,
                },
              })) || [],
            helpText: "",
            optionSize: "",
          } }
          onChange={ (event) => {
            setSelectedMealPlan(event.submitValue as PricingPlan);
          } }
          optional={ optional }
          linkingId={ linkingId }
          selected={ {
            submitValue: selectedMealPlan,
            displayValue: selectedMealPlan,
          } }
          optionStyle="confirmationModal"
        />
      ) }

      <Section
        theme="brand"
        size="sm"
        className={ "fixed bottom-0 left-0 w-full p-4 z-50" }
      >
        <Container className="flex items-center justify-center flex-col-reverse md:flex-row">
          { selectedTraySize !== currentTraySize ||
            selectedMealPlan !== currentMealPlan ? (
              <Button
                color="secondary"
                onClick={ updateSubscription }
                state={ formState }
              >
              Update my subscription
              </Button>
            ) : (
              <Button color="secondary" to="/manage-plan">
              Keep my subscription
              </Button>
            ) }
        </Container>
      </Section>

      <Modal
        isOpen={ showSuccessfullyUpdatedModal }
        onClose={ () => console.error("Cannot close") }
        hideCloseButton={ true }
        id="modal"
        component="modal"
        modalTitleId="modal"
        size="md"
      >
        <Grid className="py-12 px-8">
          <Column justify="center" align="center">
            { cat && getSelectedMealPlan && getSelectedPouch && (
              <>
                { currentMealPlan === selectedMealPlan &&
                  currentTraySize !== selectedTraySize && (
                  <>
                    <Text
                      align="center"
                      display="title"
                      size={ legacySizeCollectionMap.titleMd }
                    >
                        You&apos;ve changed { cat?.cat.name }&apos;s portion size
                    </Text>
                    <Spacer size="lg" />
                    <Text align="center">
                      { cat.cat.name } will start receiving{ " " }
                      { getIsNewPouchSizeSmaller ? "smaller" : "larger" }{ " " }
                        portions.
                      { getIsNewPouchSizeSmaller
                        ? " Use other food to cover the outstanding"
                        : ` ${cat?.cat.name}'s new calorie intake will be` }
                        :
                    </Text>
                    <Text
                      align="center"
                      size={ legacySizeCollectionMap.titleSm }
                      display="title"
                    >
                      { getIsNewPouchSizeSmaller
                        ? `${cat.suggestedCalorieAmount ?? 0 -
                          (mealPlanCalories || 0)
                        }`
                        : mealPlanCalories || 0 }{ " " }
                        kcal / day
                    </Text>
                  </>
                ) }
                { currentMealPlan !== selectedMealPlan &&
                  currentTraySize === selectedTraySize && (
                  <>
                    <Text
                      align="center"
                      display="title"
                      size={ legacySizeCollectionMap.titleMd }
                    >
                      <>
                          You&apos;ve changed { cat?.cat.name }&apos;s meal plan
                      </>
                    </Text>
                    <Spacer size="lg" />
                    <Text align="center">
                      <>{ `Because you chose our ${mealPlanTitle}
                      plan, we'll now deliver:` }</>
                    </Text>
                    <Text
                      align="center"
                      size={ legacySizeCollectionMap.titleSm }
                      display="title"
                    >
                      <>{ `${mealPlanQuantity} days worth of food every
                      ${mealPlanCadence} days` }</>
                    </Text>
                    { selectedMealPlan === PricingPlan.FREEZER && (
                      <Text align="center">
                          This will help you save freezer space.
                      </Text>
                    ) }
                  </>
                ) }
                { currentMealPlan !== selectedMealPlan &&
                  currentTraySize !== selectedTraySize && (
                  <>
                    <Text
                      align="center"
                      display="title"
                      size={ legacySizeCollectionMap.titleMd }
                    >
                      { cat?.cat.name }&apos;s new meal plan
                    </Text>
                    <Spacer size="lg" />
                    <Text align="center">
                      <>
                          From { cat.cat.gender === "BOY" ? "his" : "her" } next
                          delivery, { cat.cat.name } will start receiving:
                      </>
                    </Text>
                    <Text
                      align="center"
                      size={ legacySizeCollectionMap.titleSm }
                      display="title"
                    >
                      <>{ `${mealPlanQuantity}
                    days worth of food every ${mealPlanCadence}
                    days` }</>
                    </Text>
                  </>
                ) }
                <Text align="center">
                  <>
                    And { cat.cat.gender === "BOY" ? "he" : "she" } will get{ " " }
                    { getIsNewPouchSizeSmaller
                      ? `${(cat.suggestedCalorieAmount ?? 0) - (mealPlanCalories || 0)
                      }`
                      : `${mealPlanCalories}` }{ " " }
                    kcal from KatKin fresh per day.
                  </>
                </Text>
                <Text align="center">
                  <>
                    The total cost to feed { cat.cat.name } will be £
                    { (cat.price / 28).toFixed(2) } per day.
                  </>
                </Text>
              </>
            ) }
            <Spacer size="lg" />
            <Button to="/customer">Back to homepage</Button>
          </Column>
        </Grid>
      </Modal>
    </>
  );
};
